import type { DotStatus } from '@lmc/ui-kit/src/legacy/molecules/Legend/Legend'
import dayjs from 'dayjs'
import { reportsConstants } from 'locale/team'
import type { DueOverdueByCarerResponse } from '../components/trainingMatrix/DueOverdueTrainingMatrix'
import type { SkillMatrixResponse } from '../components/trainingMatrix/TrainingMatrixScreen'
import { getDotTitle } from './tableUtils'

type Translate = (key: string, options?: Record<string, string>) => string

type TrainingMatrixCsvTranslators = {
  /** Default namespace, where the existing `skillsAndNeeds.*` labels live. */
  t: Translate
  tTeam: Translate
}

const formatDate = (date?: string | null) =>
  date ? dayjs(date).format('DD-MM-YYYY') : ''

// Quote every field, and neutralise leading formula characters so a
// user-entered name can't execute as a spreadsheet formula.
const toCsvField = (value: string) => {
  const safe = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value

  return `"${safe.replace(/"/g, '""')}"`
}

const toCsv = (rows: string[][]) =>
  rows.map((row) => row.map(toCsvField).join(',')).join('\r\n')

// Byte order mark so Excel opens the file as UTF-8 rather than mangling
// accented names.
const UTF8_BOM = String.fromCharCode(0xfeff)

const downloadCsv = (csv: string, name: string) => {
  const blob = new Blob([UTF8_BOM, csv], { type: 'text/csv;charset=utf-8' })
  const blobUrl = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = blobUrl
  a.download = `${name}-${dayjs().format('DD-MM-YYYY')}.csv`
  document.body.appendChild(a)
  a.click()
  a.remove()
  window.URL.revokeObjectURL(blobUrl)
}

export const buildSkillMatrixCsv = (
  data: SkillMatrixResponse,
  { t, tTeam }: TrainingMatrixCsvTranslators
) => {
  const header = [
    tTeam(reportsConstants.TRAINING_MATRIX_CSV_TEAM_MEMBER),
    ...data.templates.flatMap(({ skill }) => [
      tTeam(reportsConstants.SKILL_MATRIX_CSV_STATUS, { skill: skill.name }),
      tTeam(reportsConstants.SKILL_MATRIX_CSV_COMPLETED, { skill: skill.name }),
      tTeam(reportsConstants.SKILL_MATRIX_CSV_DUE, { skill: skill.name })
    ])
  ]

  const rows = data.content.map(({ carer, carerSkills }) => [
    carer.name,
    ...data.templates.flatMap((template) => {
      const carerSkill = carerSkills[template.id]
      if (!carerSkill) {
        return ['', '', '']
      }
      const { status, trainingCompletedDate, trainingDueDate } = carerSkill
      // Mirrors getRows: the backend can still send the legacy 'overdue'.
      const dotStatus = (
        (status as string) === 'overdue' ? 'notValid' : status
      ) as DotStatus
      const title = getDotTitle(
        dotStatus,
        template.trainingRequired,
        trainingCompletedDate !== null
      )

      return [
        title ? t(`skillsAndNeeds.dots.${title}`) : '',
        formatDate(trainingCompletedDate),
        formatDate(trainingDueDate)
      ]
    })
  ])

  return toCsv([header, ...rows])
}

export const buildDueOverdueCsv = (
  data: DueOverdueByCarerResponse,
  { t, tTeam }: TrainingMatrixCsvTranslators
) => {
  const header = [
    tTeam(reportsConstants.TRAINING_MATRIX_CSV_TEAM_MEMBER),
    tTeam(reportsConstants.DUE_OVERDUE_CSV_SKILL),
    tTeam(reportsConstants.DUE_OVERDUE_CSV_TRAINING),
    tTeam(reportsConstants.DUE_OVERDUE_CSV_DUE)
  ]

  // templates, skills and trainingDueDates are parallel arrays per carer.
  const rows = data.content.flatMap((element) =>
    element.templates.map((templateId, index) => {
      const dueDate = element.trainingDueDates[index]

      return [
        data.carers[element.carer]?.name ?? '',
        data.skills[element.skills[index]]?.name ?? '',
        data.templates[templateId]?.trainingName ?? '',
        dueDate
          ? formatDate(dueDate)
          : t('skillsAndNeeds.dots.achievedDateNotSet')
      ]
    })
  )

  return toCsv([header, ...rows])
}

export const downloadSkillMatrixCsv = (
  data: SkillMatrixResponse,
  translators: TrainingMatrixCsvTranslators
) => downloadCsv(buildSkillMatrixCsv(data, translators), 'skill-matrix')

export const downloadDueOverdueCsv = (
  data: DueOverdueByCarerResponse,
  translators: TrainingMatrixCsvTranslators
) =>
  downloadCsv(buildDueOverdueCsv(data, translators), 'due-and-overdue-training')
